/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useLocation} from 'react-router';

interface MobileNavState {
    /** True while the mobile chrome (header menu button + bottom nav) is on screen. */
    available: boolean;
    /** Side drawer holding the main menu, opened from the header button. */
    menuOpen: boolean;
    /** Account sheet sliding up out of the bottom bar. */
    moreOpen: boolean;
    toggleMenu: () => void;
    toggleMore: () => void;
    closeAll: () => void;
}

const noop = () => undefined;

const MobileNavContext = createContext<MobileNavState>({
    available: false,
    menuOpen: false,
    moreOpen: false,
    toggleMenu: noop,
    toggleMore: noop,
    closeAll: noop
});

/**
 * Shared open/closed state for the two mobile menus. The menu button lives in the
 * header while the panels it controls are rendered by the bottom nav bar, so the
 * state cannot live in either component.
 */
export function useMobileNav(): MobileNavState {
    return useContext(MobileNavContext);
}

interface Props {
    available: boolean;
    children: React.ReactNode;
}

export function MobileNavProvider(props: Props) {
    const {available, children} = props;
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);

    const closeAll = useCallback(function () {
        setMenuOpen(false);
        setMoreOpen(false);
    }, []);

    const toggleMenu = useCallback(function () {
        setMoreOpen(false);
        setMenuOpen(prev => !prev);
    }, []);

    const toggleMore = useCallback(function () {
        setMenuOpen(false);
        setMoreOpen(prev => !prev);
    }, []);

    // Sidebar item clicks navigate via SidebarMenuController (which stops propagation),
    // so the drawer closes on the resulting route change rather than on click.
    useEffect(
        function () {
            closeAll();
        },
        [location.pathname, closeAll]
    );

    useEffect(
        function () {
            if (!available) closeAll();
        },
        [available, closeAll]
    );

    const value = useMemo(
        () => ({available, menuOpen, moreOpen, toggleMenu, toggleMore, closeAll}),
        [available, menuOpen, moreOpen, toggleMenu, toggleMore, closeAll]
    );

    return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
}
